import React, { useState, useEffect } from "react";
import { ClipboardList, ArrowRight, ChevronLeft, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { ficharService } from "@/services/ficharService";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Registro {
  date: number;   // unix timestamp
  action: "I" | "O" | "R" | "P";
}

export interface Correccion {
  id: string;
  estado: "PENDIENTE" | "RESUELTO" | "RECHAZADO";
  tipo: "C" | "M";
  action: "I" | "O" | "R" | "P" | null;
  motivo: string;
  creacion: string;           // "YYYY-MM-DD HH:mm"
  inicial: number;            // unix timestamp
  modificado: number | null;
  motivo_rechazo: string | null;
  data: string | null;        // JSON: Registro[] (solo tipo C — jornada propuesta)
  jornadaOriginal?: Registro[]; // se carga desde WS al abrir el detalle
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ACTION_LABELS: Record<string, string> = {
  I: "ENTRADA", O: "SALIDA", R: "REANUDACIÓN", P: "PAUSA",
};

const ACTION_BADGE: Record<string, string> = {
  I: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  O: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  R: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
  P: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
};

const ACTION_BADGE_SM: Record<string, string> = {
  I: "bg-green-500", O: "bg-red-500", R: "bg-yellow-500", P: "bg-blue-500",
};

const ESTADO_BADGE: Record<string, string> = {
  PENDIENTE: "bg-[#fff7d6] text-[#b58900]",
  RESUELTO:  "bg-[#d8f8e3] text-[#0e8a41]",
  RECHAZADO: "bg-[#f5b2b2] text-[#612222]",
};

const ESTADO_BORDER: Record<string, string> = {
  PENDIENTE: "#ca8a04", RESUELTO: "#16a34a", RECHAZADO: "#dc2626",
};

const fmtCreacion = (iso: string) =>
  new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

const fmtUnix = (ts: number, includeTime = false) =>
  new Date(ts * 1000).toLocaleDateString("es-ES", {
    day: "2-digit", month: "2-digit", year: "numeric",
    ...(includeTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });

const fmtHour = (ts: number) =>
  new Date(ts * 1000).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });

const calcHoras = (registros: Registro[]): number => {
  if (!registros?.length) return 0;
  const sorted = [...registros].sort((a, b) => a.date - b.date);
  let total = 0, inicio: number | null = null;
  for (const r of sorted) {
    const act = r.action.toUpperCase();
    if (act === "I" || act === "R") inicio = r.date;
    else if ((act === "P" || act === "O") && inicio !== null) {
      total += r.date - inicio;
      inicio = null;
    }
  }
  return total;
};

const fmtHoras = (secs: number) =>
  `${String(Math.floor(secs / 3600)).padStart(2, "0")}h ${String(Math.floor((secs % 3600) / 60)).padStart(2, "0")}m`;

// ─── Time Picker compatible con iOS ──────────────────────────────────────────
// Reemplaza <input type="time"> que se muestra mal en iOS Safari/WebView

interface TimePickerProps {
  value: string;          // formato "HH:mm"
  onChange: (v: string) => void;
}

const selectCls =
  "flex-1 border rounded-lg px-2 py-2.5 text-base text-center font-semibold " +
  "bg-background dark:bg-gray-700 dark:border-gray-600 dark:text-white " +
  "appearance-none focus:outline-none focus:ring-2 focus:ring-primary/40";

const TimePickerSelect = ({ value, onChange }: TimePickerProps) => {
  const [hh, mm] = value ? value.split(":") : ["", ""];

  const hours   = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
  const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

  const handleHour = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(`${e.target.value}:${mm || "00"}`);
  };

  const handleMinute = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(`${hh || "00"}:${e.target.value}`);
  };

  return (
    <div className="flex items-center gap-2">
      <select value={hh} onChange={handleHour} className={selectCls}>
        <option value="" disabled>HH</option>
        {hours.map((h) => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>

      <span className="text-2xl font-bold text-foreground select-none">:</span>

      <select value={mm} onChange={handleMinute} className={selectCls}>
        <option value="" disabled>MM</option>
        {minutes.map((m) => (
          <option key={m} value={m}>{m}</option>
        ))}
      </select>
    </div>
  );
};

// ─── Vista Respuesta ──────────────────────────────────────────────────────────

interface RespuestaProps {
  solicitud: Correccion;
  userHash: string;
  onVolver: () => void;
  onEnviado: () => void;
}

const RespuestaView = ({ solicitud, userHash, onVolver, onEnviado }: RespuestaProps) => {
  const { toast } = useToast();
  const isTipoC   = solicitud.tipo === "C";
  const isPending = solicitud.estado === "PENDIENTE";

  // Tipo M form state
  const [motivo, setMotivo]   = useState("");
  const [hora, setHora]       = useState("");
  const [sending, setSending] = useState(false);

  // Tipo C rechazo state
  const [showRechazo, setShowRechazo]     = useState(false);
  const [motivoRechazo, setMotivoRechazo] = useState("");
  const [charCount, setCharCount]         = useState(0);

  // Jornada original (tipo C): se carga desde WS
  const [jornadaOriginal, setJornadaOriginal]   = useState<Registro[]>([]);
  const [loadingJornada, setLoadingJornada]     = useState(isTipoC);

  // Diff para tipo C
  const nuevaPropuesta: Registro[] = isTipoC && solicitud.data
    ? JSON.parse(solicitud.data)
    : [];

  useEffect(() => {
    if (!isTipoC || !nuevaPropuesta.length) { setLoadingJornada(false); return; }
    const fecha = new Date(nuevaPropuesta[0].date * 1000).toISOString().split("T")[0];
    ficharService.getJornadaDia(userHash, fecha)
      .then((res) => setJornadaOriginal(res?.response ?? []))
      .catch(() => {})
      .finally(() => setLoadingJornada(false));
  }, []);

  // Mezclar: si mismo timestamp reemplaza action, si no existe agrega
  const jornadaMezclada: Registro[] = (() => {
    const mezclada = [...jornadaOriginal];
    for (const nuevo of nuevaPropuesta) {
      const idx = mezclada.findIndex((o) => o.date === nuevo.date);
      if (idx >= 0) mezclada[idx] = { ...mezclada[idx], action: nuevo.action };
      else mezclada.push(nuevo);
    }
    return [...mezclada].sort((a, b) => a.date - b.date);
  })();

  const segundosOrg = calcHoras(jornadaOriginal);
  const segundosNew = calcHoras(jornadaMezclada);

  const isNuevo = (reg: Registro) =>
    nuevaPropuesta.some((np) => np.date === reg.date);

  const handleAceptar = async () => {
    setSending(true);
    try {
      await ficharService.contestarCorreccion({ solicitud_id: solicitud.id, respuesta: "RESUELTO" });
      toast({ title: "Enviado", description: "La corrección fue confirmada." });
      onEnviado();
    } catch {
      toast({ title: "Error", description: "No se pudo enviar la respuesta.", variant: "destructive" });
    }
    setSending(false);
  };

  const handleEnviarCorreccion = async () => {
    if (!motivo) { toast({ title: "Error", description: "Selecciona un motivo.", variant: "destructive" }); return; }
    if (!hora)   { toast({ title: "Error", description: "Indica la hora correcta.", variant: "destructive" }); return; }
    setSending(true);
    try {
      await ficharService.contestarCorreccion({ solicitud_id: solicitud.id, respuesta: "RESUELTO", motivo, hora });
      toast({ title: "Enviado", description: "La corrección fue enviada." });
      onEnviado();
    } catch (error: any) {
      toast({ 
        title: "Error al enviar", 
        description: error.message || "No se pudo enviar la respuesta.", 
        variant: "destructive" 
      });
    } finally {
      setSending(false);
    }
  };

  const handleEnviarRechazo = async () => {
    if (motivoRechazo.trim().length < 10) {
      toast({ title: "Error", description: "El motivo debe tener al menos 10 caracteres.", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      await ficharService.contestarCorreccion({ solicitud_id: solicitud.id, respuesta: "RECHAZADO", motivo_rechazo: motivoRechazo });
      toast({ title: "Rechazado", description: "La propuesta fue rechazada." });
      onEnviado();
    } catch {
      toast({ title: "Error", description: "No se pudo enviar el rechazo.", variant: "destructive" });
    }
    setSending(false);
  };

  return (
    <div className="space-y-4 pb-6">

      {/* Botón volver */}
      <button onClick={onVolver} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ChevronLeft className="w-4 h-4" /> Volver al listado
      </button>

      {/* Cabecera */}
      <div className="rounded-2xl bg-white dark:bg-gray-800 shadow-sm border p-4 text-center space-y-2">
        <span className={`inline-block text-xs font-semibold uppercase px-3 py-1 rounded-full ${solicitud.estado === "RESUELTO" ? "bg-gray-200 text-gray-600" : "bg-[#fff7d6] text-[#b58900]"}`}>
          Estado: {solicitud.estado}
        </span>
        {isTipoC && (
          <p className="text-sm text-muted-foreground">{fmtUnix(solicitud.inicial)}</p>
        )}
      </div>

      {/* ── TIPO C: diff view ── */}
      {isTipoC && (
        <div className="rounded-xl bg-[#f4f7f6] border border-[#e0e0e0] p-4 space-y-3">

          <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-start">

            {/* Original */}
            <div className="rounded-lg bg-white dark:bg-gray-800 shadow-sm overflow-hidden min-h-[120px]">
              <div className="px-3 py-2 border-b-2 border-gray-100 text-center text-[11px] font-bold uppercase text-muted-foreground">
                Original
              </div>
              {jornadaOriginal.length === 0
                ? <p className="text-xs text-center text-muted-foreground py-4">Sin registros</p>
                : jornadaOriginal.map((r, i) => (
                  <div key={i} className="flex justify-between items-center px-3 py-2 border-b border-gray-50 text-sm text-muted-foreground">
                    <span className="line-through">{fmtHour(r.date)}</span>
                    <span className="text-xs">{ACTION_LABELS[r.action]}</span>
                  </div>
                ))
              }
            </div>

            {/* Flecha */}
            <div className="flex items-center justify-center pt-6">
              <ArrowRight className="w-5 h-5 text-muted-foreground" />
            </div>

            {/* Propuesta */}
            <div className="rounded-lg bg-white dark:bg-gray-800 shadow-sm overflow-hidden min-h-[120px] border border-green-400">
              <div className="px-3 py-2 border-b-2 bg-green-500 text-center text-[11px] font-bold uppercase text-white">
                Propuesta
              </div>
              {jornadaMezclada.map((r, i) => (
                <div key={i} className={`flex justify-between items-center px-3 py-2 border-b border-gray-50 text-sm ${isNuevo(r) ? "bg-[#ddffe3]" : ""}`}>
                  <span>{fmtHour(r.date)}{isNuevo(r) ? <span className="text-[10px] text-green-700 ml-1">← Nuevo</span> : null}</span>
                  <span className={`text-[10px] text-white px-2 py-0.5 rounded-full ${ACTION_BADGE_SM[r.action]}`}>
                    {ACTION_LABELS[r.action]}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Resumen horas */}
          <div className="rounded-lg bg-white dark:bg-gray-800 border border-dashed border-gray-300 p-3">
            <p className="text-[11px] font-bold uppercase text-center text-muted-foreground border-b pb-2 mb-2">Horas trabajadas</p>
            <div className="grid grid-cols-2 text-center">
              <div className="border-r pr-2">
                <p className="text-[10px] text-muted-foreground uppercase">Original</p>
                <p className="font-bold text-sm">{fmtHoras(segundosOrg)}</p>
              </div>
              <div className="pl-2">
                <p className="text-[10px] text-green-600 uppercase">Propuesta</p>
                <p className="font-bold text-base text-green-600">{fmtHoras(segundosNew)}</p>
              </div>
            </div>
          </div>

          {/* Motivo administrativo */}
          <div className="rounded-lg bg-white dark:bg-gray-800 border p-3 text-center">
            <p className="text-[11px] text-muted-foreground">Motivo Administrativo:</p>
            <p className="font-bold text-primary text-sm mt-1">{solicitud.motivo}</p>
          </div>
        </div>
      )}

      {/* ── TIPO M: form ── */}
      {!isTipoC && (
        <div className="rounded-2xl bg-white dark:bg-gray-800 shadow-sm border p-4 space-y-4">

          {/* Acción + hora registrada */}
          <div className="grid grid-cols-2 text-center gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase text-muted-foreground mb-1">Acción a Corregir</p>
              {solicitud.action && (
                <span className={`inline-block text-xs font-semibold uppercase px-3 py-1.5 rounded-lg w-full ${ACTION_BADGE[solicitud.action]}`}>
                  {ACTION_LABELS[solicitud.action]}
                </span>
              )}
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase text-muted-foreground mb-1">Hora Registrada</p>
              <p className="text-xl font-bold text-muted-foreground">{fmtHour(solicitud.inicial)}</p>
            </div>
          </div>

          <hr className="dark:border-gray-700" />

          {/* Motivo del cambio */}
          <div className="space-y-1">
            <label className="text-[11px] font-bold uppercase text-foreground">Motivo del Cambio</label>
            <select
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm bg-background dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="" disabled>Seleccione el motivo...</option>
              <option value="Olvido">OLVIDO</option>
              <option value="Fallo técnico o cobertura">FALLO TÉCNICO O COBERTURA</option>
              <option value="Error de registro">ERROR DE REGISTRO</option>
              <option value="Permiso de la empresa">PERMISO DE LA EMPRESA</option>
            </select>
          </div>

          {/* Hora correcta */}
          <div className="space-y-2">
            <label className="text-[11px] font-bold uppercase text-foreground">Indique la hora correcta</label>
            <TimePickerSelect value={hora} onChange={setHora} />
            <p className="text-xs text-muted-foreground italic">Esta hora sustituirá a la registrada originalmente.</p>
          </div>
        </div>
      )}

      {/* ── Botones acción (solo PENDIENTE) ── */}
      {isPending && (
        <div className="space-y-3 pt-2">

          {/* Aceptar / Enviar corrección */}
          {!showRechazo && (
            <Button
              className="w-full h-12 text-base font-semibold"
              onClick={isTipoC ? handleAceptar : handleEnviarCorreccion}
              disabled={sending}
            >
              <Check className="w-5 h-5 mr-2" />
              {isTipoC ? "Confirmar Jornada Completa" : "Enviar Corrección"}
            </Button>
          )}

          {/* No aceptar (solo tipo C) */}
          {isTipoC && !showRechazo && (
            <Button
              variant="outline"
              className="w-full border-red-400 text-red-600 hover:bg-red-50"
              onClick={() => setShowRechazo(true)}
            >
              <X className="w-4 h-4 mr-2" />
              No aceptar
            </Button>
          )}

          {/* Textarea rechazo */}
          {isTipoC && showRechazo && (
            <div className="rounded-xl border border-red-400 p-4 space-y-2">
              <label className="text-xs font-bold uppercase text-red-600">
                Explique por qué no acepta la jornada propuesta:
              </label>
              <textarea
                rows={3}
                value={motivoRechazo}
                onChange={(e) => { setMotivoRechazo(e.target.value); setCharCount(e.target.value.length); }}
                placeholder="Mínimo 10 caracteres..."
                className="w-full border rounded-lg px-3 py-2 text-sm bg-background dark:bg-gray-700 dark:border-gray-600 dark:text-white resize-none"
                maxLength={250}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Mínimo 10, máximo 250 caracteres.</span>
                <span className={charCount < 10 || charCount > 250 ? "text-red-500" : "text-green-600"}>
                  {charCount} / 250
                </span>
              </div>
              <div className="flex gap-3 pt-1">
                <Button variant="outline" className="w-1/2" onClick={() => { setShowRechazo(false); setMotivoRechazo(""); }}>
                  Cancelar
                </Button>
                <Button variant="destructive" className="w-1/2" onClick={handleEnviarRechazo} disabled={sending}>
                  Enviar Rechazo
                </Button>
              </div>
            </div>
          )}

          <p className="text-xs text-center text-muted-foreground pt-1">
            Al confirmar, los registros se actualizarán automáticamente en su historial.
          </p>
        </div>
      )}
    </div>
  );
};

// ─── Vista Listado ────────────────────────────────────────────────────────────

interface Props {
  userHash: string | null;
}

const CorreccionesFichajes = ({ userHash }: Props) => {
  const { toast } = useToast();
  const [correcciones, setCorrecciones] = useState<Correccion[]>([]);
  const [loading, setLoading]           = useState(true);
  const [detalle, setDetalle]           = useState<Correccion | null>(null);

  const cargar = async () => {
    if (!userHash) return;
    setLoading(true);
    try {
      const res = await ficharService.getCorrecciones(userHash);
      const data: Correccion[] = res.response ?? [];
      // Ordenar: PENDIENTES primero
      const sorted = [...data].sort((a, b) => (a.estado === "PENDIENTE" ? -1 : 1));
      setCorrecciones(sorted);
    } catch {
      toast({ title: "Error", description: "No se pudieron cargar las correcciones.", variant: "destructive" });
    } finally { setLoading(false); }
  };

  useEffect(() => { if (userHash) cargar(); }, [userHash]);

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <div className="w-8 h-8 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  // Vista detalle/respuesta
  if (detalle) {
    return (
      <RespuestaView
        solicitud={detalle}
        userHash={userHash ?? ""}
        onVolver={() => setDetalle(null)}
        onEnviado={() => { setDetalle(null); cargar(); }}
      />
    );
  }

  // Vista listado
  if (correcciones.length === 0) {
    return (
      <div className="text-center py-10 text-muted-foreground">
        <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-40" />
        No tienes solicitudes de corrección
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {correcciones.map((s) => {
        const isModif   = s.tipo === "M";
        const tipo      = isModif ? "Modificación" : "Creación";
        const canAnswer = s.estado === "PENDIENTE" && s.modificado === null;

        return (
          <div
            key={s.id}
            className="rounded-2xl overflow-hidden bg-white dark:bg-gray-800 shadow-md border-l-4"
            style={{ borderColor: ESTADO_BORDER[s.estado] }}
          >
            <div className="p-4 space-y-2">

              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                {isModif && (
                  <span className="text-[0.7rem] font-semibold uppercase px-2 py-1 rounded-lg bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                    Tipo solicitud: {tipo}
                  </span>
                )}
                {s.action && (
                  <span className={`text-[0.7rem] font-semibold uppercase px-2 py-1 rounded-lg ${ACTION_BADGE[s.action]}`}>
                    Tipo fichaje: {ACTION_LABELS[s.action]}
                  </span>
                )}
                <span className={`text-[0.7rem] font-semibold uppercase px-2 py-1 rounded-lg ${ESTADO_BADGE[s.estado]}`}>
                  Estado solicitud: {s.estado}
                </span>
              </div>

              <hr className="dark:border-gray-700" />

              <p className="text-sm">Motivo: <span className="italic text-muted-foreground">{s.motivo || "Sin motivo"}</span></p>
              <p className="text-sm">Solicitado: <span className="text-muted-foreground">{fmtCreacion(s.creacion)}</span></p>
              <p className="text-sm">Fichaje a corregir: <span className="text-muted-foreground">{fmtUnix(s.inicial)}</span></p>

              {isModif && (
                <>
                  <p className="text-sm">Valor anterior: <span className="text-muted-foreground">{fmtUnix(s.inicial, true)}</span></p>
                  {s.modificado && (
                    <p className="text-sm">Valor corregido: <span className="text-muted-foreground">{fmtUnix(s.modificado, true)}</span></p>
                  )}
                </>
              )}

              {s.estado === "RECHAZADO" && s.motivo_rechazo && (
                <p className="text-sm text-red-700 dark:text-red-400">
                  Motivo rechazo: <span className="italic">{s.motivo_rechazo}</span>
                </p>
              )}

              {/* Botón Responder */}
              {canAnswer && (
                <div className="pt-2 text-center">
                  <Button
                    className="w-2/3"
                    onClick={() => setDetalle(s)}
                  >
                    Responder
                  </Button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CorreccionesFichajes;
